import { useLocation, useParams } from 'react-router-dom';

import useStores from '@stores/useStores';
import { experiment, FeatureFlagKeys } from '@lib/experiment';
import { ActionType, ResourceType, useActionGuard } from '@hooks/useActionGuard';
import { getResourceTypeFromSourceDefinitionCategory } from '@hooks/useActionGuard/utils';
import {
  canConnectDestinationToExistingSource,
  canConnectDestinationToNewSource,
  canConnectSourceToExistingDestination,
  canConnectSourceToNewDestination,
  canEditDestination,
  canViewDestinationLiveEvents,
  canViewSourceLiveEvents,
} from '@hooks/useActionGuard/utils/permissionChecks';
import {
  canConnectSourceToExistingTrackingPlans,
  canCreateTrackingPlan,
} from '@components/trackingPlans/hooks/permissionChecks';
import { canViewTransformationLiveEvents } from '@components/transformations/hooks/permissionChecks';

import type { UseIsRouteEnabled } from './routeEntitlement';

/**
 * Entitlement checks for the authenticated routes, one per `enable:` rule that
 * used to live inline in the route table.
 *
 * Each is a hook called from `RouteEntitlement`. They run only when their own
 * route matches — the old table evaluated every rule on every render — so the
 * param-derived checks below read `useParams()` instead of slicing the pathname.
 *
 * Several imported `can*` helpers are hooks despite their names (they call
 * useStores/useActionGuard internally), so they must stay unconditional here.
 */

const DESTINATION_CREATE_QUERY = {
  old: { resourceType: 'destination', action: 'create' },
  new: { actionType: ActionType.CREATE, resource: { type: ResourceType.DESTINATION } },
} as const;

// --- Workspace feature entitlements -----------------------------------------

export const useHasQuickStart: UseIsRouteEnabled = () =>
  useStores().featuresStore.has('QUICKSTART');

export const useHasSqlModels: UseIsRouteEnabled = () => useStores().featuresStore.has('MODELS');

export const useHasAudiences: UseIsRouteEnabled = () => useStores().featuresStore.has('AUDIENCES');

export const useHasInvoices: UseIsRouteEnabled = () => useStores().featuresStore.has('BILLING');

export const useHasDataCatalog: UseIsRouteEnabled = () =>
  useStores().featuresStore.has('DATA_CATALOG');

export const useHasDataGraph: UseIsRouteEnabled = () => useStores().featuresStore.has('DATA_GRAPH');

/** Reporting is opt-OUT: the feature flag disables the dashboard. */
export const useHasEventMetrics: UseIsRouteEnabled = () =>
  !useStores().featuresStore.has('DISABLE_REPORTING');

// --- Source routes ----------------------------------------------------------

/**
 * The create-source page reads its definition id from `location.state`, not the
 * URL, so a direct visit without that state is not entitled — same as before.
 */
export const useCanCreateSource: UseIsRouteEnabled = () => {
  const location = useLocation();
  const { sourceDefinitionsListStore } = useStores();
  const { canPerformAction } = useActionGuard();

  const { sourceDefId = '' } = (location.state as { sourceDefId?: string } | null) ?? {};
  const sourceDef = sourceDefinitionsListStore.getById(sourceDefId);

  if (!sourceDef) {
    return false;
  }

  return canPerformAction({
    new: {
      actionType: ActionType.CREATE,
      resource: { type: getResourceTypeFromSourceDefinitionCategory(sourceDef.category) },
    },
    old: { resourceType: 'source', action: 'create' },
  }).isAllowed;
};

export const useCanViewSourceLiveEvents: UseIsRouteEnabled = () => {
  const { id = '' } = useParams<{ id: string }>();

  return canViewSourceLiveEvents(id).isAllowed;
};

export const useCanConnectSourceToExistingDestination: UseIsRouteEnabled = () => {
  const { id = '' } = useParams<{ id: string }>();

  return canConnectSourceToExistingDestination(id).isAllowed;
};

export const useCanConnectSourceToNewDestination: UseIsRouteEnabled = () => {
  const { id = '' } = useParams<{ id: string }>();

  return canConnectSourceToNewDestination(id).isAllowed;
};

// --- Destination routes -----------------------------------------------------

export const useCanCreateDestination: UseIsRouteEnabled = () =>
  useActionGuard().canPerformAction(DESTINATION_CREATE_QUERY).isAllowed;

export const useCanViewDestinationLiveEvents: UseIsRouteEnabled = () => {
  const { id = '' } = useParams<{ id: string }>();

  return canViewDestinationLiveEvents(id).isAllowed;
};

export const useCanEditDestinationMapping: UseIsRouteEnabled = () => {
  const { id = '' } = useParams<{ id: string }>();

  return canEditDestination(id).isAllowed;
};

export const useCanConnectDestinationToExistingSource: UseIsRouteEnabled = () => {
  const { id = '' } = useParams<{ id: string }>();

  return canConnectDestinationToExistingSource(id).isAllowed;
};

/** This route names its param `destinationId`, unlike its siblings. */
export const useCanConnectDestinationToNewSource: UseIsRouteEnabled = () => {
  const { destinationId = '' } = useParams<{ destinationId: string }>();

  return canConnectDestinationToNewSource(destinationId).isAllowed;
};

// --- Transformation routes --------------------------------------------------

export const useCanViewTransformationLiveEvents: UseIsRouteEnabled = () => {
  const { id = '' } = useParams<{ id: string }>();

  return canViewTransformationLiveEvents(id).isAllowed;
};

// --- Tracking plan routes ---------------------------------------------------

export const useCanCreateTrackingPlan: UseIsRouteEnabled = () => {
  const { featuresStore } = useStores();
  // Called before the `&&`: these helpers are hooks despite their names, so
  // short-circuiting them would change the hook count between renders.
  const canCreate = canCreateTrackingPlan().isAllowed;

  return featuresStore.has('DATA_CATALOG') && canCreate;
};

/** Linking a source needs edit rights on at least one source. */
export const useCanLinkSourceToTrackingPlan: UseIsRouteEnabled = () => {
  const {
    featuresStore,
    sourcesListStore,
    permissionsStore: { hasPermission },
  } = useStores();

  return (
    featuresStore.has('TRACKING_PLAN') &&
    sourcesListStore.sources.some((source) =>
      hasPermission({ resourceId: source.id, resourceType: 'source', action: 'edit' }),
    )
  );
};

export const useCanLinkTrackingPlanToSource: UseIsRouteEnabled = () => {
  const { featuresStore } = useStores();
  const { id = '' } = useParams<{ id: string }>();
  // Called before the `&&` — see useCanCreateTrackingPlan.
  const canConnect = canConnectSourceToExistingTrackingPlans(id).isAllowed;

  return featuresStore.has('TRACKING_PLAN') && canConnect;
};

// --- Feature-flagged sections ----------------------------------------------

/**
 * Read through `experiment` rather than `useFeatureFlagObserver` to keep the
 * pre-existing evaluation semantics: these resolve once per render rather than
 * reacting to a late flag flip.
 */
export const useHasAudienceCohorts: UseIsRouteEnabled = () =>
  experiment.isFeatureEnabled(FeatureFlagKeys.enableCohortAudience);

export const useIsDataMapperEnabled: UseIsRouteEnabled = () =>
  experiment.isFeatureEnabled(FeatureFlagKeys.enableDataMapper);

export const useHasProfiles: UseIsRouteEnabled = () => useStores().featuresStore.has('PROFILES');

/** The whole access management section is org-admin only, behind its rollout flag. */
export const useHasNewPermissionsModel: UseIsRouteEnabled = () => {
  const { userStore } = useStores();

  return (
    experiment.isFeatureEnabled(FeatureFlagKeys.enableNewPermissionsModel) && userStore.isOrgAdmin
  );
};

export const useHasDataGraphAudiences: UseIsRouteEnabled = () => {
  const { featuresStore } = useStores();

  return (
    experiment.isFeatureEnabled(FeatureFlagKeys.enableDataGraphAudiences) &&
    featuresStore.has('AUDIENCES')
  );
};
